import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';

const PlatformPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);

  const agents = [
    { name: 'Indicator Extraction Agent', description: 'Parses TORs and extracts measurable research indicators using advanced NLP.', capabilities: ['Document parsing', 'Concept extraction', 'Indicator validation', 'Structured output'], icon: 'pi pi-file-edit', color: '#3B82F6' },
    { name: 'Questionnaire Design Agent', description: 'Transforms indicators into structured questionnaire items with validation.', capabilities: ['Question generation', 'Response scales', 'Skip logic', 'JSON formatting'], icon: 'pi pi-list', color: '#22C55E' },
    { name: 'KoboToolbox Integration Agent', description: 'Deploys surveys to KoboToolbox and manages the form lifecycle.', capabilities: ['XLSForm conversion', 'API integration', 'Form validation', 'Deployment'], icon: 'pi pi-cloud-upload', color: '#F97316' },
    { name: 'Real-time Monitoring Agent', description: 'Continuously monitors data collection progress and quality metrics.', capabilities: ['Batch analysis', 'Quality metrics', 'Outlier detection', 'Progress tracking'], icon: 'pi pi-eye', color: '#A855F7' },
    { name: 'Data Cleaning Agent', description: 'Processes and cleans collected survey data using statistical methods.', capabilities: ['Missing value handling', 'Outlier handling', 'Data validation', 'Quality reporting'], icon: 'pi pi-wrench', color: '#06B6D4' },
    { name: 'Statistical Analysis Agent', description: 'Performs automated statistical analysis with publication-ready outputs.', capabilities: ['Univariate analysis', 'Bivariate testing', 'Visualizations', 'Dashboards'], icon: 'pi pi-chart-bar', color: '#EF4444' },
    { name: 'Report Generation Agent', description: 'Creates structured research reports with embedded charts and findings.', capabilities: ['Template reports', 'Chart embedding', 'Multiple formats', 'Summaries'], icon: 'pi pi-file-pdf', color: '#EC4899' },
    { name: 'Policy Recommendation Agent', description: 'Synthesizes evidence-based policy recommendations from findings.', capabilities: ['Evidence synthesis', 'Policy alignment', 'Implementation paths', 'Cost-benefit'], icon: 'pi pi-lightbulb', color: '#8B5CF6' }
  ];

  const architectureFeatures = [
    { title: 'AWS AgentCore Foundation', description: 'Built on AWS AgentCore with Strands SDK for multi-LLM support.', icon: 'pi pi-cloud', color: '#3B82F6' },
    { title: 'Serverless Architecture', description: 'AWS Lambda, Step Functions, and DynamoDB for auto-scaling.', icon: 'pi pi-server', color: '#22C55E' },
    { title: 'Event-Driven Processing', description: 'Cost-effective event-driven architecture with intelligent batching.', icon: 'pi pi-bolt', color: '#F97316' },
    { title: 'Enterprise Security', description: 'Customer-managed KMS encryption, VPC deployment, audit trails.', icon: 'pi pi-shield', color: '#A855F7' }
  ];

  const integrations = [
    { title: 'KoboToolbox', description: 'Native integration for survey deployment and data collection.', icon: 'pi pi-tablet', color: '#F97316', badge: 'API v2' },
    { title: 'Google OAuth', description: 'Secure authentication and user management.', icon: 'pi pi-google', color: '#3B82F6', badge: 'Enterprise SSO' },
    { title: 'Document Processing', description: 'Support for PDF, Word, and text document processing.', icon: 'pi pi-file-pdf', color: '#EF4444', badge: 'Multi-format' }
  ];

  const tabs = ['AI Agents', 'Architecture', 'Integrations', 'Security'];

  const cardStyle: React.CSSProperties = { backgroundColor: 'white', borderRadius: '8px', padding: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' };
  const iconStyle = (color: string): React.CSSProperties => ({ width: '3rem', height: '3rem', borderRadius: '8px', backgroundColor: color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 });

  return (
    <div>
      {/* Hero */}
      <div style={{ padding: '3rem 2rem', textAlign: 'center', backgroundColor: '#f8f9fa' }}>
        <h1 style={{ fontSize: '2.5rem', margin: '0 0 1rem 0', color: '#1f2937' }}>AI-Orchestrated Research Platform</h1>
        <p style={{ color: '#6b7280', fontSize: '1.125rem', maxWidth: '700px', margin: '0 auto 2rem auto' }}>
          Our platform uses 8 specialized AI agents working in coordination to automate your entire research workflow.
        </p>
        <Button label="Explore Platform" icon="pi pi-arrow-right" className="p-button-lg" onClick={() => navigate('/platform/workflow')} />
      </div>

      {/* Tabs */}
      <div style={{ padding: '2rem', backgroundColor: 'white' }}>
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '2rem', borderBottom: '1px solid #e5e7eb', paddingBottom: '1rem' }}>
          {tabs.map((tab, i) => (
            <button key={i} onClick={() => setActiveTab(i)} style={{ padding: '0.75rem 1.5rem', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 500, backgroundColor: activeTab === i ? '#3B82F6' : 'transparent', color: activeTab === i ? 'white' : '#6b7280' }}>
              {tab}
            </button>
          ))}
        </div>

        {/* AI Agents Tab */}
        {activeTab === 0 && (
          <div>
            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
              <h2 style={{ fontSize: '1.75rem', color: '#1f2937', margin: '0 0 0.5rem 0' }}>8 Specialized AI Agents</h2>
              <p style={{ color: '#6b7280' }}>Each agent handles a specific stage of the research workflow</p>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
              {agents.map((agent, i) => (
                <div key={i} style={cardStyle}>
                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <div style={iconStyle(agent.color)}><i className={agent.icon} style={{ color: 'white', fontSize: '1.25rem' }}></i></div>
                    <div style={{ flex: 1 }}>
                      <h3 style={{ fontSize: '1rem', margin: '0 0 0.5rem 0', color: '#1f2937' }}>{agent.name}</h3>
                      <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: '0 0 0.75rem 0' }}>{agent.description}</p>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                        {agent.capabilities.map((cap, j) => (
                          <span key={j} style={{ fontSize: '0.75rem', color: '#22C55E', display: 'flex', alignItems: 'center' }}>
                            <i className="pi pi-check" style={{ marginRight: '0.25rem', fontSize: '0.625rem' }}></i>{cap}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Architecture Tab */}
        {activeTab === 1 && (
          <div>
            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
              <h2 style={{ fontSize: '1.75rem', color: '#1f2937', margin: '0 0 0.5rem 0' }}>Enterprise-Grade Architecture</h2>
              <p style={{ color: '#6b7280' }}>Built on AWS with enterprise security and scalability</p>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem', marginBottom: '2rem' }}>
              {architectureFeatures.map((f, i) => (
                <div key={i} style={{ ...cardStyle, textAlign: 'center' }}>
                  <div style={{ ...iconStyle(f.color), margin: '0 auto 1rem auto', borderRadius: '50%' }}><i className={f.icon} style={{ color: 'white', fontSize: '1.25rem' }}></i></div>
                  <h3 style={{ fontSize: '1rem', margin: '0 0 0.5rem 0', color: '#1f2937' }}>{f.title}</h3>
                  <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: 0 }}>{f.description}</p>
                </div>
              ))}
            </div>
            <div style={{ ...cardStyle, backgroundColor: '#f8f9fa' }}>
              <h3 style={{ textAlign: 'center', margin: '0 0 1.5rem 0', color: '#1f2937' }}>System Architecture Overview</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
                <div style={{ textAlign: 'center', padding: '1rem', backgroundColor: '#dbeafe', borderRadius: '8px' }}>
                  <i className="pi pi-desktop" style={{ fontSize: '2rem', color: '#3B82F6', marginBottom: '0.5rem', display: 'block' }}></i>
                  <h4 style={{ margin: '0 0 0.25rem 0', color: '#1f2937' }}>Frontend Layer</h4>
                  <p style={{ margin: 0, fontSize: '0.875rem', color: '#6b7280' }}>React SPA with PrimeReact</p>
                </div>
                <div style={{ textAlign: 'center', padding: '1rem', backgroundColor: '#dcfce7', borderRadius: '8px' }}>
                  <i className="pi pi-cog" style={{ fontSize: '2rem', color: '#22C55E', marginBottom: '0.5rem', display: 'block' }}></i>
                  <h4 style={{ margin: '0 0 0.25rem 0', color: '#1f2937' }}>Agent Core</h4>
                  <p style={{ margin: 0, fontSize: '0.875rem', color: '#6b7280' }}>AWS AgentCore + Strands</p>
                </div>
                <div style={{ textAlign: 'center', padding: '1rem', backgroundColor: '#f3e8ff', borderRadius: '8px' }}>
                  <i className="pi pi-database" style={{ fontSize: '2rem', color: '#A855F7', marginBottom: '0.5rem', display: 'block' }}></i>
                  <h4 style={{ margin: '0 0 0.25rem 0', color: '#1f2937' }}>Storage Layer</h4>
                  <p style={{ margin: 0, fontSize: '0.875rem', color: '#6b7280' }}>DynamoDB, S3, KMS</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Integrations Tab */}
        {activeTab === 2 && (
          <div>
            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
              <h2 style={{ fontSize: '1.75rem', color: '#1f2937', margin: '0 0 0.5rem 0' }}>Seamless Integrations</h2>
              <p style={{ color: '#6b7280' }}>Connect with your existing tools and workflows</p>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
              {integrations.map((int, i) => (
                <div key={i} style={{ ...cardStyle, textAlign: 'center' }}>
                  <div style={{ ...iconStyle(int.color), margin: '0 auto 1rem auto', borderRadius: '50%' }}><i className={int.icon} style={{ color: 'white', fontSize: '1.25rem' }}></i></div>
                  <h3 style={{ fontSize: '1.125rem', margin: '0 0 0.5rem 0', color: '#1f2937' }}>{int.title}</h3>
                  <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: '0 0 0.75rem 0' }}>{int.description}</p>
                  <span style={{ fontSize: '0.75rem', color: '#22C55E' }}><i className="pi pi-check" style={{ marginRight: '0.25rem' }}></i>{int.badge}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Security Tab */}
        {activeTab === 3 && (
          <div>
            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
              <h2 style={{ fontSize: '1.75rem', color: '#1f2937', margin: '0 0 0.5rem 0' }}>Enterprise Security & Compliance</h2>
              <p style={{ color: '#6b7280' }}>Built with security-first principles</p>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
              <div style={cardStyle}>
                <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', margin: '0 0 1rem 0', color: '#1f2937' }}>
                  <i className="pi pi-lock" style={{ color: '#3B82F6' }}></i>Data Protection
                </h3>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {['Customer-managed KMS encryption', 'Data encryption at rest and in transit', 'VPC deployment with private subnets', 'IAM least-privilege access'].map((item, i) => (
                    <li key={i} style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem', color: '#6b7280', fontSize: '0.875rem' }}>
                      <i className="pi pi-check" style={{ color: '#22C55E', marginRight: '0.5rem' }}></i>{item}
                    </li>
                  ))}
                </ul>
              </div>
              <div style={cardStyle}>
                <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', margin: '0 0 1rem 0', color: '#1f2937' }}>
                  <i className="pi pi-file-edit" style={{ color: '#3B82F6' }}></i>Audit & Compliance
                </h3>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {['Complete audit trails', 'Reproducible workflows', 'Input validation and sanitization', 'Bedrock Guardrails integration'].map((item, i) => (
                    <li key={i} style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem', color: '#6b7280', fontSize: '0.875rem' }}>
                      <i className="pi pi-check" style={{ color: '#22C55E', marginRight: '0.5rem' }}></i>{item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* CTA */}
      <div style={{ padding: '3rem 2rem', backgroundColor: '#3B82F6', textAlign: 'center' }}>
        <h2 style={{ color: 'white', fontSize: '2rem', margin: '0 0 1rem 0' }}>Ready to See the Platform in Action?</h2>
        <p style={{ color: '#bfdbfe', fontSize: '1.125rem', maxWidth: '600px', margin: '0 auto 2rem auto' }}>
          Explore our complete workflow to see how AI agents transform your research process.
        </p>
        <Button label="View Workflow" icon="pi pi-arrow-right" className="p-button-lg" style={{ backgroundColor: 'white', color: '#3B82F6', border: 'none' }} onClick={() => navigate('/platform/workflow')} />
      </div>
    </div>
  );
};

export default PlatformPage;
